import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";

import { PageHeader } from "../shell/PageHeader";
import type { Period } from "../router/search";
import {
  useGetV1Kpi,
  useGetV1Plants,
  type Figure,
  type MetaPeriod,
  type PrVariant,
} from "@solarkey/api-client";
import { EmptyState, ErrorState, Metric, Skeleton, StateWord, Term, atDisplayScale, reasonText } from "@solarkey/ui";

/* ── F1 / screen 06: KPI overview with PR variants ──
   Region/scope Performance. Four headline KPIs (delivered energy, PR with its variant
   selector, CUF, availability), the four PR variants as small multiples (one unit each,
   never four lines on one axis), and a by-site drill-through. Real data from /v1/kpi
   (queried once per variant) and /v1/plants; the layout follows the wireframe. */

// The four variants this screen draws (the ruling adds equipment/combined later).
const SMALL_VARIANTS: PrVariant[] = ["ABSOLUTE", "GRID", "WEATHER", "GRID_WEATHER_EQUIPMENT"];

const VARIANT_META: Record<PrVariant, { chip: string; long: string; desc: string }> = {
  ABSOLUTE: { chip: "Absolute", long: "Absolute", desc: "all admitted intervals" },
  GRID: { chip: "Grid", long: "Grid-corrected", desc: "grid events excluded" },
  WEATHER: { chip: "Weather", long: "Weather-corrected", desc: "module temperature normalised" },
  EQUIPMENT: { chip: "Equipment", long: "Equipment-corrected", desc: "equipment losses removed" },
  // Named for all three corrections it applies, not two: the contract variant is
  // GRID_WEATHER_EQUIPMENT, and a shorter name would understate what it claims.
  GRID_WEATHER_EQUIPMENT: { chip: "Grid+Wx+Eq", long: "Grid, weather and equipment", desc: "all three corrections" },
};

/* The completeness floor is NOT a browser constant. UX-003 §3.7 rule 8: a derived
   status is computed on the server, from the floor in force for that period, and
   travels on the figure as its state + reason (PROVISIONAL_COVERAGE carries {pct,
   floor}). This screen therefore prints the state the server sent and never
   re-derives one from a literal of its own. */

// Period control: wireframe labels mapped to the valid period keys.
const PERIOD_TABS: { key: Period; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "mtd", label: "Month" },
  { key: "fy", label: "FY" },
];

export function Kpi({ scope, period }: { scope: string; period?: string }): React.ReactNode {
  const [prVariant, setPrVariant] = useState<PrVariant>("ABSOLUTE");
  const [availBasis, setAvailBasis] = useState<"PLANT" | "GRID">("PLANT");
  const navigate = useNavigate();
  const activePeriod = (period ?? "today") as Period;

  // One query per PR variant (React Query caches per scope/period/variant).
  const vAbsolute = useGetV1Kpi({ scope, period, pr_variant: "ABSOLUTE" });
  const vGrid = useGetV1Kpi({ scope, period, pr_variant: "GRID" });
  const vWeather = useGetV1Kpi({ scope, period, pr_variant: "WEATHER" });
  const vBoth = useGetV1Kpi({ scope, period, pr_variant: "GRID_WEATHER_EQUIPMENT" });
  const plants = useGetV1Plants({ period });

  if (vAbsolute.isLoading) {
    return (
      <div className="flex flex-col gap-[14px]" aria-busy="true">
        <Skeleton variant="text" width="12rem" />
        <div className="sk-tiles-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="tile" />)}</div>
      </div>
    );
  }

  if (vAbsolute.isError || !vAbsolute.data || vAbsolute.data.status !== 200) {
    return (
      <ErrorState title="KPI unavailable" message="The KPI overview could not be loaded." onRetry={() => void vAbsolute.refetch()} />
    );
  }

  const env = vAbsolute.data.data;
  const d = env.data;
  const availableVariants = (d.pr?.variants ?? SMALL_VARIANTS).filter((v) => SMALL_VARIANTS.includes(v));
  const shownVariants = availableVariants.length > 0 ? availableVariants : SMALL_VARIANTS;

  const variantQueries: Partial<Record<PrVariant, { isLoading: boolean; fig?: Figure }>> = {
    ABSOLUTE: { isLoading: vAbsolute.isLoading, fig: vAbsolute.data?.status === 200 ? vAbsolute.data.data.data.pr?.selected : undefined },
    GRID: { isLoading: vGrid.isLoading, fig: vGrid.data?.status === 200 ? vGrid.data.data.data.pr?.selected : undefined },
    WEATHER: { isLoading: vWeather.isLoading, fig: vWeather.data?.status === 200 ? vWeather.data.data.data.pr?.selected : undefined },
    GRID_WEATHER_EQUIPMENT: { isLoading: vBoth.isLoading, fig: vBoth.data?.status === 200 ? vBoth.data.data.data.pr?.selected : undefined },
  };
  const prOf = (v: PrVariant): Figure | undefined => variantQueries[v]?.fig;

  const selectedPr = prOf(prVariant) ?? d.pr?.selected;
  const plantsItems = plants.data?.status === 200 ? (plants.data.data.data.items ?? []) : [];
  const availabilityFigure = availBasis === "GRID" ? d.availability?.grid : d.availability?.technical;
  // The period comes from the response, never the browser clock: the backend anchors
  // every period to the latest data day, so a lagging feed must not be shown as today.
  const scopeLine = scopeLineFor(env.meta?.period);

  // PUSH: a period tab is a decision Back should undo (QA, 25 Oct).
  const setPeriod = (key: Period) =>
    void navigate({ search: ((prev: Record<string, unknown>) => ({ ...prev, period: key })) as never });

  const onExport = () =>
    exportCsv(activePeriod, scopeLine, d, shownVariants.map((v) => ({ label: VARIANT_META[v].long, fig: prOf(v) })), plantsItems);

  return (
    <div className="flex flex-col gap-[14px]">
      <PageHeader
        title="Performance"
        scope={scopeLine}
        timeControl="none"
        family="performance"
        action={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div className="sk-scenario" role="group" aria-label="Time period">
              {PERIOD_TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  data-active={activePeriod === t.key ? "true" : "false"}
                  aria-pressed={activePeriod === t.key}
                  onClick={() => setPeriod(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <button type="button" style={exportBtnStyle} onClick={onExport}>Export</button>
          </div>
        }
      />

      {/* ── Row 1: four headline KPIs ── */}
      <section aria-labelledby="kpi-tiles">
        <h2 id="kpi-tiles" className="sr-only">Headline KPIs</h2>
        <div className="sk-tiles-4">
          {/* Delivered energy */}
          <div className="sk-tile">
            <span className="sk-tile__label">Delivered energy</span>
            {d.energy_delivered ? (
              <>
                <Metric figure={atDisplayScale(d.energy_delivered) ?? d.energy_delivered} showBasis={false} showProvenance={false} showFreshness={false} maximumFractionDigits={1} />
                <span className="sk-tile__sub">{deliveredSub(d)}</span>
              </>
            ) : <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_ENGINE_ABSENT" }} />}
          </div>

          {/* PR with variant chips (annotation ①) */}
          <div className="sk-tile">
            <span className="sk-tile__label"><Term tkey="pr">PR</Term></span>
            {selectedPr ? (
              <>
                <Metric figure={selectedPr} showBasis={false} showProvenance={false} showFreshness={false} />
                <div className="sk-prbasis" role="group" aria-label="PR variant">
                  {shownVariants.map((v) => {
                    const vf = prOf(v);
                    const held = typeof vf?.value !== "number";
                    // A withheld variant (e.g. Grid+Wx+Eq until the equipment loss is
                    // supplied) carries its reason on hover, matching the four-variants
                    // panel and Home, so the headline chip never shows "Cannot check"
                    // without saying why (QA P3).
                    return (
                      <button
                        key={v}
                        type="button"
                        className="sk-prbasis__chip"
                        data-active={v === prVariant ? "true" : "false"}
                        aria-pressed={v === prVariant}
                        title={held ? reasonText(vf?.reason) ?? undefined : undefined}
                        onClick={() => setPrVariant(v)}
                      >
                        {VARIANT_META[v].chip}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_ENGINE_ABSENT" }} />}
          </div>

          {/* CUF */}
          <div className="sk-tile">
            <span className="sk-tile__label"><Term tkey="cuf">CUF</Term></span>
            {d.cuf?.ac ? (
              <>
                <Metric figure={d.cuf.ac} showBasis={false} showProvenance={false} showFreshness={false} />
                <span className="sk-tile__sub">contracted AC · uncapped</span>
              </>
            ) : <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_ENGINE_ABSENT" }} />}
          </div>

          {/* Availability with Plant/Grid chips. Ruling §6.1: the word availability
              never appears unqualified — the label names which of the three it is. */}
          <div className="sk-tile">
            <span className="sk-tile__label">
              {availBasis === "GRID"
                ? "Grid availability"
                : <Term tkey="technical_availability">Technical availability</Term>}
            </span>
            {availabilityFigure ? (
              <>
                <Metric figure={availabilityFigure} showBasis={false} showProvenance={false} showFreshness={false} />
                <span className="sk-tile__sub">
                  <span className="sk-prbasis" role="group" aria-label="Availability basis">
                    {(["PLANT", "GRID"] as const).map((b) => (
                      <button
                        key={b}
                        type="button"
                        className="sk-prbasis__chip"
                        data-active={b === availBasis ? "true" : "false"}
                        aria-pressed={b === availBasis}
                        onClick={() => setAvailBasis(b)}
                      >
                        {b === "PLANT" ? "Plant" : "Grid"}
                      </button>
                    ))}
                  </span>
                  {/* The third named availability, read from the server's own figure —
                      never asserted as a literal (ruling §6.1: it is engine-owned, the
                      platform stores and serves it and never computes it). */}
                  <span>
                    {"· energy-by-cause "}
                    {d.availability?.energy_by_cause ? (
                      <Metric
                        figure={d.availability.energy_by_cause}
                        showBasis={false}
                        showProvenance={false}
                        showFreshness={false}
                      />
                    ) : (
                      <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_ENGINE_ABSENT" }} />
                    )}
                  </span>
                </span>
              </>
            ) : <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_ENGINE_ABSENT" }} />}
          </div>
        </div>
      </section>

      {/* ── Row 2: PR small multiples + drill-through ── */}
      <div className="sk-kpi-cols">
        {/* PR, four variants side by side (annotations ②③) */}
        <section aria-labelledby="kpi-variants" className="sk-section">
          <div className="sk-section__head">
            <div>
              <h2 id="kpi-variants" className="sk-section__title">PR, four variants side by side</h2>
              <span className="sk-section__sub">one unit, one axis each</span>
            </div>
          </div>
          <div className="sk-pr-multiples">
            {shownVariants.map((v) => {
              const q = variantQueries[v];
              const meta = VARIANT_META[v];
              return (
                <div key={v} className="sk-pr-multiple">
                  <span className="sk-pr-multiple__lab">{meta.long}</span>
                  <span className="sk-pr-multiple__val">
                    {/* Every figure goes through Metric, which prints the state word and
                        its reason when the variant is withheld. A dash would read as a
                        value that happens to be missing (ruling 3.2, UX-003 §3.1). */}
                    {q?.isLoading ? (
                      <Skeleton variant="text" width="3.5rem" />
                    ) : q?.fig ? (
                      <Metric figure={q.fig} showBasis={false} showProvenance={false} showFreshness={false} maximumFractionDigits={1} />
                    ) : (
                      <StateWord state="CANNOT_CHECK" reason={{ template: "CANNOT_CHECK_VARIANT_NOT_SERVED" }} />
                    )}
                  </span>
                  {/* No trend line here: no per-day PR series endpoint exists in v1, and a
                      decorative one would be a drawn claim about data we do not have. */}
                  <span className="sk-pr-multiple__desc">{meta.desc}</span>
                </div>
              );
            })}
          </div>
          <p className="sk-pr-readas">
            Read as: what the plant did (Absolute); what the plant did when the grid let it
            (Grid); what it would have done at reference temperature (Weather); all three
            corrections together (Grid, weather and equipment).
          </p>
        </section>

        {/* Drill-through by site (annotation ④) */}
        <section aria-labelledby="kpi-drill" className="sk-section">
          <div className="sk-section__head">
            <div>
              <h2 id="kpi-drill" className="sk-section__title">Drill-through</h2>
              <span className="sk-section__sub">PR {VARIANT_META[prVariant].chip} · by site</span>
            </div>
          </div>
          <KpiDrillThrough loading={plants.isLoading} items={plantsItems} />
        </section>
      </div>
    </div>
  );
}

type DrillItem = { site_id?: string | null; pr?: Figure | null; coverage?: Figure | null };

function KpiDrillThrough(
  { loading, items }: { loading: boolean; items: DrillItem[] },
): React.ReactNode {
  if (loading) return <Skeleton variant="row" lines={4} />;

  const withPr = items.filter((s) => typeof s.pr?.value === "number");
  if (items.length === 0) return <EmptyState variant="no_data" title="No sites in scope" />;
  if (withPr.length === 0) {
    return (
      <EmptyState
        variant="no_data"
        title="No site has a PR figure for this period"
        message="Ranking needs at least one measured PR; none of the sites in scope carries one."
      />
    );
  }
  const withoutPr = items.length - withPr.length;

  const median = medianOf(withPr.map((s) => s.pr!.value as number));
  const ranked = [...withPr].sort(
    (a, b) => Math.abs((b.pr!.value as number) - median) - Math.abs((a.pr!.value as number) - median),
  );
  const top = ranked.slice(0, 4);
  const more = ranked.length - top.length;

  return (
    <>
      <table className="sk-set-table sk-kpi-drill">
        <thead>
          <tr>
            <th>Site</th>
            <th className="sk-kpi-drill__num">PR</th>
            <th className="sk-kpi-drill__num">Coverage</th>
            <th>State</th>
          </tr>
        </thead>
        <tbody>
          {top.map((s, i) => (
            <tr key={s.site_id ?? i}>
              <td>{s.site_id ?? <StateWord state="CANNOT_CHECK" reason={{ template: "CANNOT_CHECK_NO_SITE_ID" }} />}</td>
              {/* Both figures render through Metric: the server decides whether a PR is
                  MEASURED, PROVISIONAL or withheld, and against which floor. */}
              <td className="sk-kpi-drill__num">
                {/* Small table text, not the metric headline size (wireframe ④):
                    the drill-through is a list, not a KPI tile. PR drops its unit
                    (the column says PR); coverage keeps the % inline. */}
                <Metric figure={s.pr!} showBasis={false} showProvenance={false} showFreshness={false} maximumFractionDigits={1} className="sk-metric--table" />
              </td>
              <td className="sk-kpi-drill__num">
                {s.coverage ? (
                  <Metric figure={s.coverage} showBasis={false} showProvenance={false} showFreshness={false} maximumFractionDigits={0} className="sk-metric--inline" />
                ) : (
                  <StateWord state="CANNOT_CHECK" reason={{ template: "CANNOT_CHECK_NO_COVERAGE" }} />
                )}
              </td>
              {/* The state is the figure's own, with its reason. No basis chip: this row
                  carries no designated energy basis, and METER is not a safe default —
                  two of the three plants in the estate are INVERTER_DERIVED. */}
              <td>{stateCell(s.pr!)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {more > 0 || withoutPr > 0 ? (
        <p className="sk-set-note">
          {more > 0 ? `${more} more · sorted by deviation from the region median` : "sorted by deviation from the region median"}
          {withoutPr > 0 ? ` · ${withoutPr} site${withoutPr === 1 ? "" : "s"} without a PR figure, not ranked` : ""}
        </p>
      ) : null}
    </>
  );
}

function stateCell(pr: Figure): React.ReactNode {
  return <StateWord state={pr.state} reason={pr.reason} />;
}

function deliveredSub(d: { energy_delivered?: Figure; reconciliation_ratio?: Figure }): React.ReactNode {
  const basis = d.energy_delivered?.basis;
  const cov = typeof d.energy_delivered?.coverage_pct === "number" ? d.energy_delivered.coverage_pct : null;
  const recon = typeof d.reconciliation_ratio?.value === "number" ? d.reconciliation_ratio.value : null;
  return (
    <>
      {basis ? <span className="sk-basis-chip">{basis}</span> : null}
      {cov !== null ? <span>{cov}%</span> : null}
      {recon !== null ? <span>· reconciled {recon}%</span> : null}
    </>
  );
}

/* The period the server resolved, printed from meta.period. The backend anchors
   every period to the latest data day, so the browser clock must not name the
   window (a lagging feed would otherwise be labelled "today"). */
function scopeLineFor(period: MetaPeriod | null | undefined): string {
  if (!period?.start) return "Period not stated · ruled definitions";
  const start = fmtIsoDay(period.start);
  const end = period.end ? fmtIsoDay(period.end) : null;
  const window = !end || end === start ? start : `${start} to ${end}`;
  return `${window} · ruled definitions`;
}

/** Format a YYYY-MM-DD day from the server without crossing a timezone. */
function fmtIsoDay(iso: string): string {
  const [y, m, dd] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !dd) return iso;
  return new Date(y, m - 1, dd).toLocaleDateString(undefined, {
    day: "numeric", month: "short", year: "numeric",
  });
}

/** Download the current Performance view as a CSV (what's on screen; real values once a backend serves them). */
function exportCsv(
  period: Period,
  scopeLine: string,
  d: {
    energy_delivered?: Figure;
    cuf?: { ac?: Figure };
    availability?: { technical?: Figure; grid?: Figure; energy_by_cause?: Figure };
  },
  variants: { label: string; fig?: Figure }[],
  sites: DrillItem[],
): void {
  // A withheld figure exports its state word, never an empty cell that reads as zero.
  const num = (f?: Figure) => (typeof f?.value === "number" ? String(f.value) : "");
  const state = (f?: Figure) => (f ? (typeof f.value === "number" ? f.state ?? "" : f.state ?? "CANNOT_CHECK") : "CANNOT_CHECK");
  const rows: string[][] = [
    ["Performance", scopeLine],
    [],
    ["KPI", "Value", "Unit", "State"],
    ["Delivered energy", num(d.energy_delivered), d.energy_delivered?.unit ?? "", state(d.energy_delivered)],
    ["CUF (AC, uncapped)", num(d.cuf?.ac), d.cuf?.ac?.unit ?? "", state(d.cuf?.ac)],
    ["Technical availability", num(d.availability?.technical), d.availability?.technical?.unit ?? "", state(d.availability?.technical)],
    ["Grid availability", num(d.availability?.grid), d.availability?.grid?.unit ?? "", state(d.availability?.grid)],
    ["Energy availability by cause", num(d.availability?.energy_by_cause), d.availability?.energy_by_cause?.unit ?? "", state(d.availability?.energy_by_cause)],
    [],
    ["PR variant", "Value (%)", "State"],
    ...variants.map((v) => [v.label, num(v.fig), state(v.fig)]),
    [],
    ["Site", "PR", "Coverage", "State"],
    ...sites.map((s) => [
      s.site_id ?? "",
      num(s.pr ?? undefined),
      num(s.coverage ?? undefined),
      state(s.pr ?? undefined),
    ]),
  ];
  const csv = rows.map((r) => r.map(csvCell).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `performance-${period}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function csvCell(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

function medianOf(xs: number[]): number {
  if (xs.length === 0) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid]! : (s[mid - 1]! + s[mid]!) / 2;
}

const exportBtnStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  height: 30,
  padding: "0 12px",
  fontSize: "12px",
  fontWeight: 600,
  border: "1px solid var(--sk-color-border-2)",
  borderRadius: "var(--sk-radius-control)",
  background: "var(--sk-color-surface-0)",
  color: "var(--sk-color-ink-1)",
  cursor: "pointer",
  whiteSpace: "nowrap",
};
